import React, { useState, useEffect } from "react";
import { X, Info } from "lucide-react";
import frameBg from "../../../Assets/FrameBGDashboard.png";
import { createTicket } from "../../../Services/TicketService";
import NotificationAlert from "../../Alerts/NotificationAlert";
import { useNavigate } from "react-router-dom";
import { GetProjectData } from "../services/ClientServices";
import { CreateExternalTicket } from "../services/ClientServices";

const CreateTicketModal = ({ email, show, onClose, onCreated }) => {
    const navigate = useNavigate();
    const [newTicket, setNewTicket] = useState({
        Title: "",
        Descritption: "",
        TicketType: "",
        Severity: "",
        ProjectUID: "",
    });

    const [attachments, setAttachments] = useState([]);
    const [ticketTypes, setTicketTypes] = useState([]);
    const [projects, setProjects] = useState([]);
    const [severityTypes, setSeverityTypes] = useState([]);
    const [alert, setAlert] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);

    //close handler
    const handleClose = () => {
        onClose();
    };

    // Load metadata once
    useEffect(() => {
        if(show) loadProject();
    }, [show]);

    // Reset form when modal opens again
    useEffect(() => {
        if (show) {
            setNewTicket({
                Title: "",
                Descritption: "",
                TicketType: "1",
                Severity: "1",
                ProjectUID: "",
            });

            setAttachments([]);
            setErrors({});
            setAlert(null);
            setLoading(false);
        }
    }, [show]);

    if (!show) return null;

    const loadProject = async () => {
        try {
            const response = await GetProjectData(email);
            setProjects(response.data?.data || []);
        } catch (err) {
            if (err.response?.status === 400) {
                navigate("/bad-request", {
                    state: {
                        error: err.response?.data?.message || "Bad request (400)",
                    },
                });
                return;
            }
            console.error("Error loading projects", err);
        }
    };

    // Handle input
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewTicket((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        if (!newTicket.Title.trim()) newErrors.Title = "Title is required";
        if (!newTicket.Descritption.trim())
            newErrors.Descritption = "Description is required";
        //if (!newTicket.TicketType) newErrors.TicketType = "Ticket Type is required";
        //if (!newTicket.Severity) newErrors.Severity = "Severity is required";
        if (!newTicket.ProjectUID.trim())
            newErrors.ProjectUID = "Project UID is required";
        return newErrors;
    };

    // Handle multiple file upload (max 5 files)
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);

        // TOTAL FILE COUNT CHECK (MAX 5)
        const totalFiles = attachments.length + files.length;
        if (totalFiles > 5) {
            setAlert({
                type: "error",
                message: "You can upload a maximum of 5 attachments.",
            });
            e.target.value = "";
            return;
        }

        // Allowed MIME types
        const allowedTypes = [
            "image/jpeg",
            "image/png",
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ];

        // Backend size limits
        const sizeLimits = {
            "image/jpeg": 200 * 1024,
            "image/png": 200 * 1024,
            "application/pdf": 5 * 1024 * 1024,
            "application/msword": 2 * 1024 * 1024,
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                2 * 1024 * 1024,
            "application/vnd.ms-excel": 2 * 1024 * 1024,
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                2 * 1024 * 1024,
        };

        // Validate each file
        for (let file of files) {
            if (!allowedTypes.includes(file.type)) {
                setAlert({
                    type: "error",
                    message:
                        "Invalid attachment. Allowed file types and sizes: Image(JPEG, PNG) ≤ 200 KB, Word ≤ 2 MB, Excel ≤ 2MB, PDF ≤ 5 MB.",
                });
                e.target.value = "";
                return;
            }
            if (file.size > sizeLimits[file.type]) {
                setAlert({
                    type: "error",
                    message:
                        "Invalid attachment. Allowed file types and sizes: Image(JPEG, PNG) ≤ 200 KB, Word ≤ 2 MB, Excel ≤ 2MB, PDF ≤ 5 MB.",
                });
                e.target.value = "";
                return;
            }
        }
        setAttachments((prev) => [...prev, ...files]);
        e.target.value = "";
    };

    const removeAttachment = (index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    // Submit
    const handleAddTicket = async () => {
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length) {
            setErrors(validationErrors);
            return;
        }

        try {
            setLoading(true);

            if (!email) {
                setAlert({
                    type: "error",
                    message: "User email not found. Please login again.",
                });
                setLoading(false);
                return;
            }
            const formData = new FormData();

            Object.keys(newTicket).forEach((key) =>
                formData.append(key, newTicket[key])
            );
            formData.append("CreatedByEmail", email);

            attachments.forEach((file) => formData.append("Attchement", file));

            const response = await CreateExternalTicket(formData);

            if (response.data?.status === "Success") {
                setAlert({
                    type: "success",
                    message: response.data?.message || "Ticket created successfully",
                });

                setTimeout(() => {
                    onCreated?.();
                    onClose();
                }, 2000);
            } else {
                setAlert({
                    type: "error",
                    message: response.data?.message || "Failed to create ticket",
                });
            }
        } catch (error) {
            if (error.response?.status === 400) {
                navigate("/bad-request", {
                    state: {
                        error: error.response?.data?.message || "Bad request (400)",
                    },
                });
                return;
            }
            setAlert({
                type: "error",
                message:
                    error.response?.data?.message ||
                    error.response?.data?.Message ||
                    "Server error",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-end z-50 transition-opacity duration-300 opacity-100"
            onClick={onClose}
        >
            <div
                className="bg-white  w-full max-w-4xl h-full shadow-2xl border-l animate-slideFadeIn flex flex-col overflow-hidden rounded-l-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div
                    className="flex justify-between items-center px-8 py-6 border-b p-5 text-white shadow-md bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${frameBg})` }}
                >
                    <h2 className="text-2xl font-semibold">Create New Ticket</h2>
                    <button onClick={onClose}>
                        <X size={26} />
                    </button>
                </div>

                {/* Form */}
                <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* Title */}
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Title <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="Title"
                                value={newTicket.Title}
                                onChange={handleInputChange}
                                className={`w-full p-2 border rounded-md mt-1 text-sm ${errors.Title ? "border-red-500" : ""
                                    }`}
                                placeholder="Enter ticket title"
                            />
                            {errors.Title && (
                                <p className="text-xs text-red-500 mt-1">{errors.Title}</p>
                            )}
                        </div>

                        {/* Description */}
                        <div className="col-span-2">
                            <div className="flex justify-between items-center mt-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Description <span className="text-red-500">*</span>
                                </label>

                            </div>
                            <div className="relative">
                                {aiLoading ? (
                                    <div className="w-full h-[120px] rounded-md bg-gray-200 animate-pulse flex flex-col gap-2 p-3">
                                        <div className="h-3 bg-gray-300 rounded w-3/4"></div>
                                        <div className="h-3 bg-gray-300 rounded w-full"></div>
                                        <div className="h-3 bg-gray-300 rounded w-5/6"></div>
                                        <div className="h-3 bg-gray-300 rounded w-2/3"></div>
                                    </div>
                                ) : (
                                    <textarea
                                        name="Descritption"
                                        value={newTicket.Descritption}
                                        onChange={handleInputChange}
                                        rows={4}
                                        className={`w-full p-2 border rounded-md mt-1 text-sm ${errors.Descritption ? "border-red-500" : ""
                                            }`}
                                        placeholder="Enter ticket description"
                                    />
                                )}
                            </div>
                            {errors.Descritption && (
                                <p className="text-xs text-red-500 mt-1">
                                    {errors.Descritption}
                                </p>
                            )}
                        </div>

                        {/* Ticket Type */}
                        <div>
                            <label className="w-full block text-sm font-medium text-gray-700">
                                Ticket Type <span className="text-red-500">*</span>
                            </label>
                            <input
                            name="Ticket Type"
                                type="text"
                                value="Bug"
                                disabled
                             onChange={handleInputChange}
                             className={`w-full p-2 border rounded-md mt-1 text-sm cursor-not-allowed ${
                               errors.TicketType ? "border-red-500" : ""
                             }`}
                            />


                            {errors.TicketType && (
                                <p className="text-xs text-red-500 mt-1">{errors.TicketType}</p>
                            )}
                        </div>

                        {/* Severity */}
                        <div>
                            <label className="w-full  block text-sm font-medium  text-gray-700 ">
                                Severity <span className="text-red-500">*</span>
                            </label>
                            <input
                            name="Severity"
                                type="text"
                                value="High"
                                disabled
                             onChange={handleInputChange}
                             className={`w-full p-2 border rounded-md mt-1 text-sm cursor-not-allowed ${
                               errors.Severity ? "border-red-500" : ""
                             }`}
                            />
                            {/*</div><option value="">Select severity</option>
                {severityTypes.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>*/}
                            {errors.Severity && (
                                <p className="text-xs text-red-500 mt-1">{errors.Severity}</p>
                            )}
                        </div>

                        {/* Project Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Project Name <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="ProjectUID"
                                value={newTicket.ProjectUID}
                                onChange={handleInputChange}
                                className={`w-full p-2 border rounded-md mt-1 text-sm ${errors.ProjectUID ? "border-red-500" : ""
                                    }`}
                            >
                                <option value="">Select Project</option>

                                {projects.map((proj) => (
                                    <option key={proj.projectUID} value={proj.projectUID}>
                                        {proj.projectName}
                                    </option>
                                ))}
                            </select>

                            {errors.ProjectUID && (
                                <p className="text-xs text-red-500 mt-1">{errors.ProjectUID}</p>
                            )}
                        </div>

                        {/* Attachment */}
                        {/* <div>
              <label className="block text-sm font-medium text-gray-700">
                Attachment
              </label>
              <input
                type="file"
                name="Attchement"
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
                multiple
                className="w-full p-2 border rounded-md mt-1 text-sm"
              />
            </div> */}
                        <div>
                            <div className="flex items-center gap-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Attachment
                                </label>

                                {/* Info Icon with Tooltip */}
                                <div className="relative group">
                                    <Info
                                        size={16}
                                        className="text-gray-500 cursor-pointer hover:text-gray-700"
                                    />
                                </div>
                            </div>

                            <input
                                type="file"
                                name="Attchement"
                                onChange={handleFileChange}
                                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
                                multiple
                                className="w-full p-2 border rounded-md mt-1 text-sm"
                            />
                        </div>

                        {/* Attachment Preview List */}
                        {attachments.length > 0 && (
                            <div className="col-span-2 mt-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                    Attachments ({attachments.length})
                                </h3>

                                <div className="w-full bg-gray-50 border rounded-lg p-4 max-h-48 overflow-y-auto">
                                    {attachments.map((file, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between p-2 bg-white rounded-md shadow-sm border mb-2"
                                        >
                                            {/* File Name & Icon */}
                                            <div className="flex items-center gap-3">
                                                {file.type.startsWith("image/") ? (
                                                    <img
                                                        src={URL.createObjectURL(file)}
                                                        className="w-12 h-12 rounded object-cover border"
                                                        alt="preview"
                                                    />
                                                ) : (
                                                    <div className="w-12 h-12 flex items-center justify-center bg-gray-200 rounded text-gray-600 text-sm">
                                                        {file.name.split(".").pop().toUpperCase()}
                                                    </div>
                                                )}

                                                {/* File Info */}
                                                <div>
                                                    <p className="text-sm font-medium text-gray-800">
                                                        {file.name}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {(file.size / 1024).toFixed(1)} KB
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Remove */}
                                            <button
                                                onClick={() => removeAttachment(index)}
                                                className="text-red-500 hover:text-red-700 text-sm font-medium"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-8 py-4 border-t bg-white flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 text-sm border rounded-md text-gray-600 hover:bg-gray-100"
                        disabled={loading}
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleAddTicket}
                        className="px-6 py-2 text-sm bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md shadow hover:opacity-90"
                        disabled={loading}
                    >
                        {loading ? "Creating..." : "Add Ticket"}
                    </button>
                </div>
            </div>

            {alert && (
                <NotificationAlert
                    type={alert.type}
                    message={alert.message}
                    show={true}
                    duration={4000}
                    onClose={() => setAlert(null)}
                />
            )}
        </div>
    );
};

export default CreateTicketModal;
